// notifications.service.ts
import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import {
  Messages_Event_Type_Enum,
  MessengerThread,
  NotificationsResponseModel,
} from 'src/app/models/owner-dashboard';
import { NotificationElement } from '../../models/dashboard.shared';
import { ApiService } from './lsg.api.service';
import { AffiliateApi } from './affiliate.api.service';
import { PortalSocketService } from './portal-socket.service';
import { UserDashboardService } from './user-dashboard.service';
import { SharedMessengerService } from '../partials/messenger/shared-messenger.service';

import {
  NotificationsActions,
  newNotifications,
} from '../store/_features/notifications/notifications.actions';
import {
  selectNotifications,
  selectUnreadNotificationsCount,
} from '../store/_features/notifications/notifications.selector';
import { NotificationToastComponent } from './notification-toast/notification-toast.component';
import { MessageToastComponent } from './message-toast/message-toast.component';
import { AnnouncementToastComponent } from './announcement-toast/announcement-toast.component';

interface LoadedState {
  notifications: boolean;
  announcements: boolean;
  messages: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationsService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  // Audio
  private readonly notificationSound = new Audio('assets/notification.mp3');
  private readonly seenSound = new Audio('assets/seen.mp3');

  // State
  ownerNotifications: NotificationsResponseModel =
    this.createEmptyNotifications();
  affiliateNotifications: NotificationsResponseModel =
    this.createEmptyNotifications();

  loaded: LoadedState = {
    announcements: false,
    notifications: false,
    messages: false,
  };

  // Real-time signals
  private readonly _newCommentNotification$ =
    new Subject<NotificationElement>();
  public readonly newCommentNotification$ =
    this._newCommentNotification$.asObservable();

  // Idempotency sets
  private processedNotificationIds = new Set<number>();
  private processedMessageKeys = new Set<string>();

  // Toast configurations
  private readonly toastConfigs = {
    message: {
      toastComponent: MessageToastComponent,
      tapToDismiss: false,
      toastClass: 'ip-custom-toast',
      timeOut: 4200,
      extendedTimeOut: 1100,
    },
    notification: {
      toastComponent: NotificationToastComponent,
      tapToDismiss: false,
      toastClass: 'ip-custom-toast',
      timeOut: 4200,
      extendedTimeOut: 1100,
    },
    announcement: {
      toastComponent: AnnouncementToastComponent,
      tapToDismiss: false,
      toastClass: 'ip-custom-toast',
      timeOut: 4200,
      extendedTimeOut: 1100,
    },
  };

  constructor(
    private api: ApiService,
    private affiliateApi: AffiliateApi,
    private toastr: ToastrService,
    private socketService: PortalSocketService,
    private store: Store,
    public messengerService: SharedMessengerService,
    public userDashboardService: UserDashboardService,
  ) {
    this.initializeStoreSubscriptions();
    this.initializeSocketListeners();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize store subscriptions
   */
  private initializeStoreSubscriptions(): void {
    this.store
      .select(selectNotifications)
      .pipe(takeUntil(this.destroy$))
      .subscribe((notifications) => {
        this.ownerNotifications.notifications = notifications;
      });

    this.store
      .select(selectUnreadNotificationsCount)
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        this.ownerNotifications.unreadNotificationsCount = count;
      });
  }

  /**
   * Initialize socket listeners
   */
  private initializeSocketListeners(): void {
    this.socketService.socketReady$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.startOwnerNotificationsListening();
        this.startMessengerListening();
      });
  }

  /**
   * Create empty notifications object
   */
  private createEmptyNotifications(): NotificationsResponseModel {
    return {
      notifications: [],
      unreadInvitaionsCount: 0,
      unreadNotificationsCount: 0,
      unreadMessagesCount: 0,
      unreadAnnouncementsCount: 0,
      announcements: [],
      invitations: [],
    };
  }

  // Audio Management
  playNotificationSound(): void {
    this.notificationSound.play().catch((error) => {
      console.warn('Could not play notification sound:', error);
    });
  }

  playSeenSound(): void {
    this.seenSound.play().catch((error) => {
      console.warn('Could not play seen sound:', error);
    });
  }

  // Owner Notifications
  currentOwnerFilter: string | undefined = undefined;

  // Owner Notifications
  getOwnerNotificationsList(filterType?: string): void {
    this.currentOwnerFilter = filterType;
    this.store.dispatch(NotificationsActions.load({ filterType }));
  }

  loadMoreOwnerNotifications(
    offset: number,
    limit: number = 10,
    filterType?: string,
  ): void {
    // If filterType passed explicitly use it, otherwise use current
    const filter =
      filterType !== undefined ? filterType : this.currentOwnerFilter;
    this.store.dispatch(
      NotificationsActions.loadMore({ offset, limit, filterType: filter }),
    );
  }

  private _ownerNotificationsSub: Subscription;

  private startOwnerNotificationsListening(): void {
    if (this._ownerNotificationsSub) {
      this._ownerNotificationsSub.unsubscribe();
    }

    this._ownerNotificationsSub = this.socketService.notifications$
      .pipe(
        filter((notification) => !!notification),
        takeUntil(this.destroy$),
      )
      .subscribe((notification: NotificationElement) => {
        this.handleNewOwnerNotification(notification);
      });
  }

  private handleNewOwnerNotification(notification: NotificationElement): void {
    if (this.processedNotificationIds.has(notification.id)) {
      return;
    }
    this.processedNotificationIds.add(notification.id);

    this.store.dispatch(newNotifications({ notification }));
    this.playNotificationSound();
    this.triggerNotificationToast(notification);

    if (
      notification.type === 'new_comment' ||
      notification.type === 'comment_reply'
    ) {
      this._newCommentNotification$.next(notification);
    }
  }

  // Affiliate Notifications
  async getAffiliateNotificationsList(): Promise<void> {
    this.loaded.notifications = false;
    this.loaded.announcements = false;

    try {
      const notifications = await this.affiliateApi
        .getAffiliateNotifications(
          false,
          undefined,
          undefined,
          this.handleError,
        )
        .toPromise();

      if (notifications) {
        this.affiliateNotifications = notifications;
        this.loaded.announcements = true;
        this.loaded.notifications = true;
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  async loadMoreAffiliateNotifications(
    offset: number,
    limit: number = 10,
  ): Promise<void> {
    try {
      const result = await this.affiliateApi
        .getAffiliateNotifications(false, limit, offset, this.handleError)
        .toPromise();

      if (result && result.notifications) {
        // Prevent duplicates
        const newNotifications = result.notifications.filter(
          (n) =>
            !this.affiliateNotifications.notifications.some(
              (existing) => existing.id === n.id,
            ),
        );
        this.affiliateNotifications.notifications = [
          ...this.affiliateNotifications.notifications,
          ...newNotifications,
        ];
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  // Messenger
  private _messengerSub: Subscription;

  private startMessengerListening(): void {
    if (this._messengerSub) {
      this._messengerSub.unsubscribe();
    }

    this._messengerSub = this.socketService.messenger$
      .pipe(
        filter((thread) => !!thread),
        takeUntil(this.destroy$),
      )
      .subscribe((thread: MessengerThread) => {
        this.handleMessengerEvent(thread);
      });
  }

  private handleMessengerEvent(thread: MessengerThread): void {
    if (thread.type !== this.userDashboardService.dashboardSessionMode) {
      this.userDashboardService.enableOtherSideNotification();
      return;
    }

    if (thread.eventType === Messages_Event_Type_Enum.newMessage) {
      this.handleNewMessage(thread);
    } else if (thread.eventType === Messages_Event_Type_Enum.seenUpdate) {
      this.handleSeenUpdate(thread);
    }
  }

  private handleNewMessage(thread: MessengerThread): void {
    this.messengerService.handleNewMessage(thread);

    if (thread.id !== this.messengerService.messengerData?.currentThread?.id) {
      // Create a unique key for this message event
      const messageKey = `${thread.id}_${thread.lastActivity?.time}`;

      if (this.processedMessageKeys.has(messageKey)) {
        return;
      }
      this.processedMessageKeys.add(messageKey);

      this.incrementNetworkMessageCount(thread.users[0]?.id);
      this.playNotificationSound();
      this.triggerMessengerToast(thread);
    }
    // Note: If it IS the current thread, we don't show toast, but we still might want to mark as processed if we were tracking strict message deduplication.
    // However, the issue is about "toast stays showing multiple times". The toast logic is inside the `if` block.
    // So guarding the `if` block contents is correct for preventing duplicate toasts.
  }

  private handleSeenUpdate(thread: MessengerThread): void {
    this.messengerService.handleNewSeen(thread);
    this.playSeenSound();
  }

  private incrementNetworkMessageCount(networkId: number): void {
    if (!networkId) return;

    // const networks = this.affiliateDashboardService.myOwnersList.joinedNetworks;
    // const networkIndex = networks.findIndex((n) => n.id === networkId);

    // if (networkIndex !== -1) {
    //   networks[networkIndex].unreadMessagesCount++;
    // }
  }

  // Toast Notifications
  private triggerNotificationToast(notification: NotificationElement): void {
    const toast: ActiveToast<any> = this.toastr.show(
      '',
      '',
      this.toastConfigs.notification,
    );
    toast.toastRef.componentInstance.notificationObject = notification;
  }

  private triggerMessengerToast(thread: MessengerThread): void {
    const toast: ActiveToast<any> = this.toastr.show(
      '',
      '',
      this.toastConfigs.message,
    );
    toast.toastRef.componentInstance.thread = thread;
  }

  private triggerAnnouncementToast(announcement: NotificationElement): void {
    const toast: ActiveToast<any> = this.toastr.show(
      '',
      '',
      this.toastConfigs.announcement,
    );
    toast.toastRef.componentInstance.announcement = announcement;
  }

  // Error Handling
  private handleError(error?: any): void {
    console.error('Notifications error:', error);
  }

  // Cleanup (these are now handled by takeUntil in the service)
  endOwnerNotificationsListening(): void {
    // Handled by takeUntil(this.destroy$)
  }

  endMessengerListening(): void {
    // Handled by takeUntil(this.destroy$)
  }
}
